// The ONLY way the admin talks to the server. Same-origin cookies; every call carries
// X-Requested-With (CSRF guard) and X-Store-Id (current store). Errors throw ApiError.
// Never spread a response into state without checking its shape — an HTML body from a
// missing route must fail loudly, not render garbage.
import type { ApiError as ApiErrorBody } from '@shared/types'

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public fields?: Record<string, string>) {
    super(message)
  }
}

let currentStoreId: string | null = null
try { currentStoreId = localStorage.getItem('storeId') } catch { /* private mode */ }

export function getStoreId() { return currentStoreId }
export function setStoreId(id: string | null) {
  currentStoreId = id
  try { if (id) localStorage.setItem('storeId', id); else localStorage.removeItem('storeId') } catch { /* ignore */ }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

async function request<T>(method: Method, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'X-Requested-With': 'fetch', Accept: 'application/json' }
  if (currentStoreId) headers['X-Store-Id'] = currentStoreId
  let payload: BodyInit | undefined
  if (body instanceof FormData) payload = body
  else if (body !== undefined) { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(body) }
  const res = await fetch(path, { method, headers: { ...headers, ...(init.headers as any) }, body: payload, credentials: 'same-origin', signal: init.signal })
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    if (res.ok && res.status === 204) return undefined as T
    throw new ApiError(res.status, 'bad_response', res.ok ? 'Unexpected response from server.' : `Request failed (${res.status}).`)
  }
  const data = await res.json()
  if (!res.ok) {
    const e = (data as ApiErrorBody).error
    if (res.status === 401 && e?.code !== 'reauth_required' && !path.startsWith('/api/auth/')) window.dispatchEvent(new CustomEvent('auth:expired'))
    throw new ApiError(res.status, e?.code ?? 'error', e?.message ?? 'Something went wrong.', e?.fields)
  }
  return data as T
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>('GET', path, undefined, init),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, form),
}

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) continue
    u.set(k, Array.isArray(v) ? v.join(',') : String(v))
  }
  const s = u.toString()
  return s ? `?${s}` : ''
}

/**
 * For navigations that cannot send headers (EventSource, <a href> downloads, window.open for print
 * views): appends ?storeId=. Server routes opt in with requireStore(perm, { allowQuery: true }).
 */
export function withStore(path: string): string {
  return path + (path.includes('?') ? '&' : '?') + 'storeId=' + encodeURIComponent(currentStoreId ?? '')
}

/** Download a file through fetch (so headers are sent) and save it. */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(path, { headers: { 'X-Requested-With': 'fetch', ...(currentStoreId ? { 'X-Store-Id': currentStoreId } : {}) }, credentials: 'same-origin' })
  if (!res.ok) throw new ApiError(res.status, 'download_failed', 'Download failed.')
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/** Only same-app relative paths are allowed as post-login redirects (no //evil.com, no /\evil.com). */
export const safeNext = (p: unknown): string =>
  typeof p === 'string' && /^\/(?![\/\\])/.test(p) && !/[\s\u0000-\u001f\u007f\\]/.test(p) && !p.includes('//') ? p : '/'
